/** Single grid row renderer.
 *
 * Ported (behaviour-preserving) from the `ConfigRowItem` in
 * `../AssistedConfigEditor.tsx`. Indentation by depth, expand/collapse chevron
 * for groups, the type-aware value cell, a per-row delete button, a
 * batch-select checkbox, and click-to-select-for-help. An integration key the
 * tour operator may change itself (`x-client: edit`) carries a "client-editable"
 * badge.
 */
import { Box, Checkbox, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { ReactNode } from 'react';

import { ValueCell } from './ValueCell';
import type { ConfigRow } from './types';

/** Optional per-row custom editor. Return a node to override the default value
 *  cell (and any group expansion), or null to fall back to the built-in cell. */
export type RenderValueFn = (
  row: ConfigRow,
  onChange: (v: unknown) => void,
  readOnly: boolean,
) => ReactNode | null;

const ROW_HEIGHT = 40;
const INDENT = 24;

export interface ConfigRowItemProps {
  row: ConfigRow;
  selected: boolean;
  /** Value cell is read-only (still allows row deletion). */
  readOnly: boolean;
  /** Whether to render the delete button + selection checkbox. */
  deletable: boolean;
  checked: boolean;
  onSelect: () => void;
  onToggleExpand: () => void;
  onChange: (v: unknown) => void;
  onRequestDelete: () => void;
  onToggleCheck: () => void;
  /** Optional custom-widget renderer (see RenderValueFn). */
  renderValue?: RenderValueFn;
  /** Advisory config-schema warning for this field (highlights the row, non-blocking). */
  warning?: string;
}

export function ConfigRowItem({
  row,
  selected,
  readOnly,
  deletable,
  checked,
  onSelect,
  onToggleExpand,
  onChange,
  onRequestDelete,
  onToggleCheck,
  renderValue,
  warning,
}: ConfigRowItemProps) {
  const custom = renderValue?.(row, onChange, readOnly || row.isUnknown) ?? null;
  return (
    <Box
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        minHeight: ROW_HEIGHT,
        px: 1,
        pl: `${row.depth * INDENT + 4}px`,
        gap: 1,
        cursor: 'pointer',
        borderRadius: 1,
        bgcolor: selected ? 'action.selected' : warning ? 'rgba(237, 108, 2, 0.09)' : 'transparent',
        '&:hover': {
          bgcolor: selected ? 'action.selected' : warning ? 'rgba(237, 108, 2, 0.14)' : 'action.hover',
        },
        borderLeft: '3px solid',
        borderLeftColor: warning ? 'warning.main' : 'transparent',
        borderBottom: '1px solid',
        borderBottomColor: 'divider',
      }}
    >
      {/* Expand chevron */}
      <Box sx={{ width: 28, flexShrink: 0 }}>
        {row.isGroup ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            sx={{ p: 0.25 }}
          >
            {row.isExpanded ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
        ) : null}
      </Box>

      {/* Key name + inline type badge, e.g. "api_key <string>" */}
      <Box
        sx={{
          width: 320,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          minWidth: 0,
        }}
      >
        <Tooltip
          title={row.key}
          placement="top-start"
          arrow
          disableHoverListener={row.key.length < 32}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              fontWeight: row.depth === 0 ? 600 : 400,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
              textDecoration: row.schema?.deprecated ? 'line-through' : 'none',
            }}
          >
            {row.key}
          </Typography>
        </Tooltip>
        {row.schema?.type && (
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontFamily: 'monospace', fontSize: 11, flexShrink: 0 }}
          >
            {`<${row.schema.type}>`}
          </Typography>
        )}
        {row.schema?.deprecated && (
          <Tooltip title="Deprecated — prefer the more specific keys">
            <Chip
              label="deprecated"
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 16, fontSize: 9, flexShrink: 0 }}
            />
          </Tooltip>
        )}
        {row.schema?.['x-client'] === 'edit' && (
          <Tooltip title="The tour operator's own users can change it in the dashboard (Developer › Integrations)">
            <Chip
              label="client-editable"
              icon={<EditOutlinedIcon />}
              size="small"
              variant="outlined"
              sx={{ height: 16, fontSize: 9, flexShrink: 0, '& .MuiChip-icon': { fontSize: 11, ml: '4px' } }}
            />
          </Tooltip>
        )}
        {row.isUnknown && (
          <Tooltip title="Not in schema">
            <WarningAmberIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0 }} />
          </Tooltip>
        )}
        {warning && (
          <Tooltip title={warning}>
            <ErrorOutlineIcon sx={{ fontSize: 15, color: 'warning.main', flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>

      {/* Value cell: custom widget → default cell → group summary */}
      <Box sx={{ flex: 1, minWidth: 0, py: 0.5 }} onClick={(e) => e.stopPropagation()}>
        {custom != null ? (
          custom
        ) : !row.isGroup ? (
          <ValueCell
            prop={row.schema}
            value={row.value}
            readOnly={readOnly || row.isUnknown}
            onChange={onChange}
          />
        ) : (
          <Typography variant="caption" color="text.disabled">
            {row.isExpanded
              ? 'expanded'
              : `{${Object.keys((row.value as Record<string, unknown>) ?? {}).length} keys}`}
          </Typography>
        )}
      </Box>

      {/* Per-row delete button */}
      <Box sx={{ width: 36, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        {deletable && (
          <Tooltip title={`Delete ${row.path.join('.')}`} placement="top" arrow>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete();
              }}
              sx={{ p: 0.25, color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Right-most: selection checkbox for batch deletion */}
      <Box sx={{ width: 36, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        {deletable && (
          <Checkbox
            size="small"
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={onToggleCheck}
            sx={{ p: 0.25 }}
          />
        )}
      </Box>
    </Box>
  );
}
